/**
 * パスワードの控えと、再設定の依頼(2026-09-03 発注者指示)。
 *
 * 発注者の判断により、登録・変更されたパスワードを承認者が確認できるようにする。
 * 値は users とは別の `user_secrets` に置き、**承認者(マスタ管理権限)だけが読める**
 * ようセキュリティルールで制限する(一般ユーザーからは読めない)。
 *
 * ※パスワードをそのまま保存する方式は、データが漏れたときの影響が大きい。
 *   運用上のリスクは発注者に説明済み(2026-09-03)。
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { Timestamp } from 'firebase/firestore';

/** パスワードの控え(user_secrets/{uid}) */
export type UserSecret = {
  id: string;
  /** いまのパスワード(平文) */
  password: string;
  /** 誰が設定したか。admin=承認者が発行 / self=本人が変更 */
  set_by: 'admin' | 'self';
  /** 設定した人の表示名 */
  set_by_name?: string;
  updated_at?: Timestamp;
};

/** パスワード再設定の依頼(password_reset_requests/{自動ID}) */
export type ResetRequest = {
  id: string;
  login_id: string;
  note?: string;
  created_at?: Timestamp;
};

export async function fetchUserSecrets(): Promise<Record<string, UserSecret>> {
  const snap = await getDocs(collection(getDb(), 'user_secrets'));
  const out: Record<string, UserSecret> = {};
  snap.docs.forEach((d) => {
    out[d.id] = { id: d.id, ...(d.data() as Omit<UserSecret, 'id'>) };
  });
  return out;
}

/** パスワードの控えを残す(パスワードを設定・変更したときに必ず呼ぶ) */
export async function recordPassword(
  uid: string,
  password: string,
  setBy: 'admin' | 'self',
  setByName?: string,
): Promise<void> {
  await setDoc(
    doc(getDb(), 'user_secrets', uid),
    { password, set_by: setBy, set_by_name: setByName ?? null, updated_at: serverTimestamp() },
    { merge: true },
  );
}

export async function deleteUserSecret(uid: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'user_secrets', uid));
}

/**
 * 初期パスワードの既定値(2026-09-03 指示で全社統一)。
 * app_secrets/account に置き、承認者だけが読める。
 * ソースに書くと配信するJSから誰でも読めてしまうため、ここでは持たない。
 */
export async function fetchDefaultPassword(): Promise<string> {
  const snap = await getDoc(doc(getDb(), 'app_secrets', 'account'));
  return (snap.data() as { default_password?: string } | undefined)?.default_password ?? '';
}

// --- パスワードを忘れたときの依頼 -------------------------------------------

/** ログイン画面から出す依頼(ログイン前なので、書き込みだけ許可している) */
export async function createResetRequest(loginId: string, note?: string): Promise<void> {
  const id = `${loginId}-${Date.now()}`;
  await setDoc(doc(getDb(), 'password_reset_requests', id), {
    login_id: loginId,
    note: note ?? '',
    created_at: serverTimestamp(),
  });
}

export async function fetchResetRequests(): Promise<ResetRequest[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'password_reset_requests'), orderBy('created_at', 'desc')),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ResetRequest, 'id'>) }));
}

export async function deleteResetRequest(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'password_reset_requests', id));
}
